import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import braincare from 'components/routes/braincare';
import { showErrorToast, showSuccessToast } from 'features/toastSlice';
import Subtitle from 'components/Subtitle';
import SectionLoader from 'components/SectionLoader';
import Button from 'components/Button';
import App from 'pages/braincare/organizations/template';
import { useNavigate, useParams } from 'react-router';
import { updateOrganization } from 'api/portal';
import { useFetchOrganizationAdmin } from 'hooks/useFetchOrganizationAdmin';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { castValueToNumber, dayFieldString, filterNumbers } from 'utils/inputs';
import { storedRefreshToExtraDayString } from 'utils/deviceJwtExpiration';
import Fade from 'components/Fade';

function AdminOrganizationDeviceJwtExpiration() {
  const [organization, setOrganization] = useState(null);
  const [jwtDays, setJwtDays] = useState('');
  const [refreshDays, setRefreshDays] = useState('');
  const [errors, setErrors] = useState(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { orgId } = params;
  const { t } = useTranslation();

  const p = 'organization.organization-settings.device-jwt-expiration';

  useFetchOrganizationAdmin({
    orgId,
    onSuccess: (data) => {
      setOrganization(data);
    },
    onFailure: () => {
      navigate('/braincare/organizations');
      dispatch(showErrorToast('messages.load-failure'));
    },
  });

  useEffect(() => {
    if (!organization?.id) {
      return;
    }
    setJwtDays(dayFieldString(organization.jwt_device_expiration));
    setRefreshDays(
      storedRefreshToExtraDayString(
        organization.jwt_device_expiration,
        organization.refresh_device_expiration,
      ),
    );
  }, [organization?.id, organization?.jwt_device_expiration, organization?.refresh_device_expiration]);

  const validateForm = () => {
    const nextErrors = new Map();
    const jwtNum = castValueToNumber(jwtDays);
    const refreshNum = castValueToNumber(refreshDays);

    if (jwtNum === undefined) {
      nextErrors.set('jwt', t(`${p}.field-required`));
    } else if (jwtNum < 1) {
      nextErrors.set('jwt', t(`${p}.access-token-min`));
    }

    if (refreshNum === undefined) {
      nextErrors.set('refresh', t(`${p}.field-required`));
    } else if (refreshNum < 30) {
      nextErrors.set('refresh', t(`${p}.refresh-token-min`));
    }

    return nextErrors;
  };

  const onSubmit = () => {
    const nextErrors = validateForm();
    setErrors(nextErrors);

    if (nextErrors.size > 0) {
      dispatch(showErrorToast('messages.fix-fields'));
      return;
    }

    const jwtNum = castValueToNumber(jwtDays);
    const refreshNum = castValueToNumber(refreshDays);
    const refreshTotal = jwtNum + refreshNum;

    setIsSubmitting(true);

    updateOrganization(orgId, {
      jwt_device_expiration: jwtNum,
      refresh_device_expiration: refreshTotal,
    })
      .then((response) => {
        setIsSubmitting(false);
        dispatch(showSuccessToast('organization.organization-settings.save-success-message'));
        if (response.data) {
          setOrganization(response.data);
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        dispatch(showErrorToast('organization.organization-settings.save-failure-message'));
      });
  };

  const orgName = organization?.name || '';

  return (
    <App title={orgName}>
      <Fade>
        <Box maxWidth="428px" marginBottom="48px">
          <Subtitle>{t(`${p}.subtitle`)}</Subtitle>

          <Typography variant="body2" color="secondary" marginBottom="24px">
            {t(`${p}.intro`)}
          </Typography>

          <SectionLoader loading={!organization}>
            <Fade timeout={500}>
              <TextField
                id="jwt_device_expiration"
                sx={{ marginTop: 2 }}
                value={jwtDays}
                onChange={(e) => setJwtDays(filterNumbers(e.target.value))}
                label={t(`${p}.access-token-label`)}
                variant="outlined"
                error={errors.has('jwt')}
                helperText={errors.get('jwt')}
                fullWidth
                inputProps={{ inputMode: 'numeric', 'data-testid': 'admin-jwt-device-expiration-input' }}
              />

              <TextField
                id="refresh_device_expiration"
                sx={{ marginTop: 3 }}
                value={refreshDays}
                onChange={(e) => setRefreshDays(filterNumbers(e.target.value))}
                label={t(`${p}.refresh-token-label`)}
                variant="outlined"
                error={errors.has('refresh')}
                helperText={errors.get('refresh')}
                fullWidth
                inputProps={{ inputMode: 'numeric', 'data-testid': 'admin-refresh-device-expiration-input' }}
              />

              <Button
                variant="outlined"
                loading={isSubmitting}
                onClick={onSubmit}
                sx={{ marginTop: 4 }}
                data-testid="save-button"
              >
                {t(`${p}.submit-button-text`)}
              </Button>
              <Button
                sx={{ marginLeft: 2, marginTop: 4 }}
                variant="outlined"
                onClick={() => navigate('/braincare/organizations')}
                data-testid="cancel-button"
              >
                {t(`${p}.cancel-button-text`)}
              </Button>
            </Fade>
          </SectionLoader>
        </Box>
      </Fade>
    </App>
  );
}

export default braincare(AdminOrganizationDeviceJwtExpiration);
